/**
 * AppLayout.tsx  -  Main shell for authenticated teacher portal.
 *
 * Seven sections grouped by the job, not by feature. Sections with more than
 * one page (Teach, Reports) show their pages as pill tabs above the content.
 */

import { ConfirmEmailBanner } from "@/components/ConfirmEmailBanner";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Home,
  Users,
  BookOpen,
  CheckSquare,
  BarChart2,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  AlertTriangle,
  Building2,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useLogout } from "@/hooks/useAuth";
import {
  useUnreadCount,
  usePendingSubmissions,
  useLicenseSummary,
} from "@/services/api";
import LitlingsMark from "@/components/LitlingsMark";
import { SkipLink } from "@/components/SkipLink";

interface Section {
  to: string;
  icon: typeof Home;
  label: string;
  match: string[];
  tabs?: { to: string; label: string }[];
}

const SECTIONS: Section[] = [
  { to: "/dashboard", icon: Home, label: "Today", match: ["/dashboard"] },
  {
    to: "/submissions",
    icon: CheckSquare,
    label: "Marking",
    match: ["/submissions"],
  },
  {
    to: "/classes",
    icon: Users,
    label: "Classes",
    match: ["/classes", "/boss-battle", "/sessions"],
  },
  {
    to: "/planner",
    icon: BookOpen,
    label: "Teach",
    match: ["/planner", "/lessons", "/spelling", "/games", "/resources"],
    tabs: [
      { to: "/planner", label: "Planner" },
      { to: "/lessons", label: "Lessons" },
      { to: "/spelling", label: "Spelling" },
      { to: "/games", label: "Games" },
      { to: "/resources", label: "Resources" },
    ],
  },
  {
    to: "/progress",
    icon: BarChart2,
    label: "Reports",
    match: ["/progress", "/certificates"],
    tabs: [
      { to: "/progress", label: "Progress" },
      { to: "/certificates", label: "Certificates" },
    ],
  },
  {
    to: "/messages",
    icon: MessageSquare,
    label: "Messages",
    match: ["/messages"],
  },
  { to: "/settings", icon: Settings, label: "Settings", match: ["/settings"] },
];

const FOOTER_LINKS: [string, string][] = [
  ["/privacy", "Privacy"],
  ["/terms", "Terms"],
  ["/cookies", "Cookies"],
  ["/safeguarding", "Safeguarding"],
  ["/security", "Security"],
  ["/dpa", "DPA"],
  ["mailto:hello@litlings.com", "Contact"],
];

function sectionFor(path: string): Section | undefined {
  return SECTIONS.find((s) =>
    s.match.some((m) => path === m || path.startsWith(m + "/")),
  );
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const { pathname } = useLocation();
  const current = sectionFor(pathname);
  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: submissionData } = usePendingSubmissions();
  const pendingSubmissions = submissionData?.submissions?.length ?? 0;
  const { data: licenseData } = useLicenseSummary();
  const trialDaysLeft =
    licenseData?.daysLeft != null &&
    licenseData.daysLeft > 0 &&
    licenseData.daysLeft <= 14 &&
    !licenseData.autoRenew
      ? licenseData.daysLeft
      : null;

  const counts: Record<string, number> = {
    "/submissions": pendingSubmissions,
    "/messages": unreadCount,
  };

  return (
    <div className="flex h-screen overflow-hidden bg-portal-bg">
      <SkipLink />
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 flex w-60 flex-col border-r border-portal-border bg-portal-bg-alt transition-transform duration-300 lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-20 items-center gap-3 px-6">
          <LitlingsMark size={32} />
          <p className="font-display text-xl font-extrabold tracking-tight text-portal-text">
            Litlings
          </p>
        </div>

        <nav
          aria-label="Main navigation"
          className="flex-1 space-y-1 overflow-y-auto px-4"
        >
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = current?.to === s.to;
            const n = counts[s.to] ?? 0;
            return (
              <NavLink
                key={s.to}
                to={s.to}
                onClick={() => setSidebarOpen(false)}
                aria-current={active ? "page" : undefined}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold transition-colors ${
                  active
                    ? "bg-brand-ink text-white"
                    : "text-portal-text-soft hover:bg-white hover:text-portal-text"
                }`}
              >
                <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                <span className="flex-1">{s.label}</span>
                {n > 0 && (
                  <span className="rounded-full bg-brand-red px-2 py-0.5 text-[11px] font-bold tabular-nums text-white">
                    {n > 99 ? "99+" : n}
                    <span className="sr-only"> waiting</span>
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4">
          {user?.role === "school_admin" && (
            <NavLink
              to="/school"
              className="mb-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold text-portal-text-soft hover:bg-white hover:text-portal-text"
            >
              <Building2 className="h-[18px] w-[18px]" />
              School office
            </NavLink>
          )}
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-brand-yellow text-sm font-bold text-brand-ink">
              {user?.firstName?.[0]}
              {user?.lastName?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-portal-text">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs text-portal-muted">
                {user?.school?.name}
              </p>
            </div>
          </div>
          <button
            onClick={() => logout.mutate()}
            className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-portal-muted transition-colors hover:bg-white hover:text-danger"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-portal-border bg-white px-4 lg:hidden">
          <button
            className="rounded-full p-2 text-portal-text hover:bg-portal-bg-alt"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            {sidebarOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
          <p className="font-display text-lg font-extrabold">Litlings</p>
        </header>

        <ConfirmEmailBanner />
        {trialDaysLeft !== null && (
          <div
            className={`flex flex-shrink-0 items-center justify-between gap-4 px-6 py-2.5 text-sm font-semibold ${
              trialDaysLeft <= 3
                ? "bg-brand-red text-white"
                : "bg-brand-yellow text-brand-ink"
            }`}
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {`Your ${licenseData?.plan === "trial" ? "trial" : "plan"} ends in ${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}. After that you move to the free plan: up to 40 pupils, AI marking off. Nothing is deleted.`}
            </span>
            <a
              href="/school/billing"
              className="whitespace-nowrap rounded-full border border-current px-3 py-1 text-xs font-bold transition-colors hover:bg-white/20"
            >
              Upgrade
            </a>
          </div>
        )}

        <main
          id="main-content"
          className="portal-main flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 px-5 py-6 sm:px-8 sm:py-8">
            {current?.tabs && (
              <nav
                aria-label={`${current.label} sections`}
                className="mb-6 flex flex-wrap gap-2"
              >
                {current.tabs.map((t) => (
                  <NavLink
                    key={t.to}
                    to={t.to}
                    className={({ isActive }) =>
                      `rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                        isActive || pathname.startsWith(t.to + "/")
                          ? "bg-brand-ink text-white"
                          : "bg-portal-bg-alt text-portal-text-soft hover:text-portal-text"
                      }`
                    }
                  >
                    {t.label}
                  </NavLink>
                ))}
              </nav>
            )}
            <Outlet />
          </div>
          <footer className="flex flex-shrink-0 flex-col items-center justify-between gap-2 border-t border-portal-border px-8 py-3 text-xs text-portal-muted sm:flex-row">
            <span>&copy; 2026 Litlings</span>
            <nav className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <a
                href="mailto:safeguarding@litlings.com?subject=Safeguarding%20concern"
                className="font-semibold text-danger hover:text-danger-ink"
              >
                Report a concern
              </a>
              {FOOTER_LINKS.map(([href, label]) => (
                <a key={href} href={href} className="hover:text-portal-text">
                  {label}
                </a>
              ))}
            </nav>
          </footer>
        </main>
      </div>
    </div>
  );
}
